/* eslint-disable prettier/prettier */
import React from 'react'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { showToast } from './toastSlice'
import makeRequest from 'src/services/api'
import handleGeneralError from '../helpers/errorHandler'
import {
  fetchAllCountries,
  addCountries,
  uploadAppLogo,
  fetchCountryConfigUrl,
  fetchCountryFeaturesUrl,
} from 'src/services/urls'
import { getUniqueListBy } from 'src/lib/utils'
import { fetchLoyaltyMasterConfig } from './configurationSlice'

const initialState = {
  loading: false,
  countryConfig: {},
  countryConfigError: '',
  data: { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 },
  docs: [],
  // docs: [{ countryCode: 'TH', country: 'Thailand' }, { countryCode: 'IN', country: 'India' }],
  error: '',
  activePage: 0,
  features: {}
}

export const fetchCountries = createAsyncThunk(
  'countries/fetchCountries',
  async (_, { dispatch }) => {
    try {
      const response = await makeRequest(fetchAllCountries())
      let docs = []
      if (response.data) {
        docs = response.data.data.docs
      }
      return { docs, data: response.data.data }
    } catch (e) {
      if (e?.response?.status === 500)
        dispatch(
          showToast({
            title: e?.response?.data?.status,
            message: e?.response?.data?.error?.message,
            color: 'danger',
          }),
        )
      // else dispatch(showToast({ title: 'Error!', message: 'Something went wrong. Please try again.', color: 'danger' }))
      // return { error: e?.response?.data?.error?.message }
    }
  },
)

export const fetchCountryFeatures = createAsyncThunk(
  'countries/fetchCountryFeatures',
  async (_, { dispatch }) => {
    try {
      const response = await makeRequest(fetchCountryFeaturesUrl())
      let features = {}
      if (response.data) {
        features = response.data.data
      }
      return { features }
    } catch (e) {
      if (e?.response?.status === 500)
        dispatch(
          showToast({
            title: e?.response?.data?.status,
            message: e?.response?.data?.error?.message,
            color: 'danger',
          }),
        )
      // else dispatch(showToast({ title: 'Error!', message: 'Something went wrong. Please try again.', color: 'danger' }))
      // return { error: e?.response?.data?.error?.message }
    }
  },
)

export const fetchCountryConfig = createAsyncThunk(
  'countries/fetchCountryConfig',
  async (_, { dispatch }) => {
    try {
      const response = await makeRequest(fetchCountryConfigUrl())
      let docs = []
      if (response.data) {
        docs = response.data.data.docs
      }

      return { docs, data: response.data.data }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const addCountry = createAsyncThunk('countries/addCountry', async (data, { dispatch }) => {
  try {
    const mapper = (arr) => {
      return arr.map((item) => item.value)
    }
    const body = {
      ...data,
      businessUnit: mapper(data.businessUnit),
      domain: mapper(data.domain),
      // languages: mapper(data.languages),
    }
    const response = await makeRequest(addCountries(), 'POST', body)

    const { displayMessage, status } = response.data

    if (response.data.statusCode === 201) {
      dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      dispatch(fetchCountries())
    }

    return response.data
  } catch (e) {
    handleGeneralError(e, { dispatch, showToast })
  }
})

export const uploadImage = createAsyncThunk('countries/uploadImage', async (data, { dispatch }) => {
  try {
    const formData = new FormData()
    formData.append('id', data.id)
    formData.append('module', data.module)
    const configType = data && data.configType ? data.configType : null
    delete data.configType
    if (data.default) formData.append('default', data.default)
    formData.append('file', data.file)

    const response = await makeRequest(uploadAppLogo(), 'POST', formData)
    const { displayMessage, status } = response.data

    if (response.data.statusCode === 200) {
      dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      dispatch(fetchCountries())
      if (configType && configType !== 'banner') {
        dispatch(fetchLoyaltyMasterConfig({ type: configType }))
      }
    }
    return response.data
  } catch (e) {
    handleGeneralError(e, { dispatch, showToast })
  }
})

const countrySlice = createSlice({
  name: 'countries',
  initialState,
  reducers: {
    setActivePage: (state, action) => {
      state.activePage = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCountries.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchCountries.fulfilled, (state, action) => {
      state.loading = false
      state.error = action.payload.error
      if (!action.payload.error) {
        state.data = action.payload.data
        const oldData = action.payload.filtered ? [] : state.docs
        const totalData = [...oldData, ...action.payload.docs]
        const uniqueData = getUniqueListBy(totalData, '_id')
        state.docs = uniqueData
      }
    })
    builder.addCase(fetchCountries.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(fetchCountryConfig.fulfilled, (state, action) => {
      state.loading = false
      state.countryConfig = action.payload?.docs?.[0]
      state.countryConfigError = ''
    })
    builder.addCase(fetchCountryConfig.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.countryConfigError = action.error.message
    })
    builder.addCase(addCountry.fulfilled, (state, action) => {
      state.loading = false
      // state.data = action.payload
      state.error = ''
    })
    builder.addCase(addCountry.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.error = action.error.message
    })
    builder.addCase(uploadImage.fulfilled, (state, action) => {
      state.loading = false
      // state.data = action.payload
      state.error = ''
    })
    builder.addCase(uploadImage.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.error = action.error.message
    })
    builder.addCase(fetchCountryFeatures.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchCountryFeatures.fulfilled, (state, action) => {
      state.loading = false
      state.error = action.payload.error
      if (!action.payload.error) {
        state.features = action.payload.features
      }
    })
    builder.addCase(fetchCountryFeatures.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
  },
})

export default countrySlice.reducer
export const { setActivePage } = countrySlice.actions
